/** Structured fields for the standard LTS email signature layout. */
export type EmailSignatureFields = {
  logoUrl: string
  logoAlt: string
  address: string
  websiteUrl: string
  websiteLabel: string
  footer: string
  disclaimer: string
}

export const DEFAULT_SIGNATURE_FIELDS: EmailSignatureFields = {
  logoUrl: 'https://static.wixstatic.com/media/9612e5_e494e3e78d4e42f9a014109e7bcdc58e~mv2.png',
  logoAlt: 'Larson Tower Specialists',
  address: '3261 W 12600 S, Riverton, UT 84065',
  websiteUrl: 'https://www.larsontower.com',
  websiteLabel: 'www.larsontower.com',
  footer: 'Larson Tower Specialists • Ridiculously Consistent',
  disclaimer:
    'This email and its attachments contain confidential and proprietary information belonging to Larson Tower Specialists LLC and its affiliates. The information may be subject to legal privilege and is intended only for the use of the intended recipient. If you are not the intended recipient, you are prohibited from disseminating, distributing, or copying this communication. If you have received this email in error, please notify us immediately and destroy all copies. Thank you for your cooperation.',
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function unescapeHtml(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')
}

/** Build the standard signature HTML from editable fields + recipient placeholders. */
export function buildSignatureHtml(fields: EmailSignatureFields): string {
  const logoUrl = escapeHtml(fields.logoUrl.trim())
  const logoAlt = escapeHtml(fields.logoAlt.trim() || 'Larson Tower Specialists')
  const address = escapeHtml(fields.address.trim())
  const websiteUrl = escapeHtml(fields.websiteUrl.trim())
  const websiteLabel = escapeHtml(fields.websiteLabel.trim() || fields.websiteUrl.trim())
  const footer = escapeHtml(fields.footer.trim())
  const disclaimer = escapeHtml(fields.disclaimer.trim())

  return `<div style="font-family: Arial, Helvetica, sans-serif; font-size: 13px; color: #222; max-width: 520px;">

  <table cellpadding="0" cellspacing="0" border="0" style="width: 100%; border-collapse: collapse;">
    <tr>
      
      <!-- Left: Logo -->
      <td valign="middle" style="width: 190px; padding-right: 15px;">
        <img src="${logoUrl}"
             alt="${logoAlt}"
             style="display: block; width: 170px; height: auto; border: 0;">
      </td>

      <!-- Divider -->
      <td style="width: 1px; background-color: #cccccc;"></td>

      <!-- Right: Contact Info -->
      <td valign="middle" style="padding-left: 15px;">
        
        <!-- Name -->
        <p style="margin: 0; font-size: 15px; font-weight: bold; color: #000;">
          {{name}}
        </p>

        <!-- Title -->
        <p style="margin: 2px 0 8px 0; color: #555;">
          {{position}}
        </p>

        <!-- Contact -->
        <p style="margin: 0 0 6px 0;">
          <a href="tel:{{phone}}" style="color: #1a5d6b; text-decoration: none;">
            {{phone}}
          </a>
          <span style="color: #999;"> | </span>
          <a href="mailto:{{email}}" style="color: #1a5d6b; text-decoration: none;">
            {{email}}
          </a>
        </p>

        <!-- Address -->
        <p style="margin: 0; color: #777; font-size: 12px;">
          ${address}
        </p>

        <!-- Website -->
        <p style="margin: 4px 0 0 0;">
          <a href="${websiteUrl}" target="_blank" style="color: #1a5d6b; text-decoration: none;">
            ${websiteLabel}
          </a>
        </p>

      </td>
    </tr>
  </table>

  <!-- Footer -->
  <div style="border-top: 1px solid #eeeeee; margin-top: 16px; padding-top: 10px; font-size: 11px; color: #999;">
    ${footer}
  </div>

  <!-- Legal Disclaimer -->
  <div style="margin-top: 10px; font-size: 10px; line-height: 1.4; color: #888;">
    ${disclaimer}
  </div>

</div>`
}

export const DEFAULT_SIGNATURE_HTML = buildSignatureHtml(DEFAULT_SIGNATURE_FIELDS)

/**
 * Best-effort parse of standard-layout HTML back into form fields.
 * Returns null when the HTML does not look like the standard template.
 */
export function parseSignatureFields(html: string): EmailSignatureFields | null {
  if (!html.includes('{{name}}') || !html.includes('{{position}}')) return null

  const logoMatch = html.match(/<img[^>]*\ssrc="([^"]*)"[^>]*>/i)
  const logoAltMatch = html.match(/<img[^>]*\salt="([^"]*)"[^>]*>/i)
  const addressMatch = html.match(
    /<!-- Address -->\s*<p[^>]*>\s*([\s\S]*?)\s*<\/p>/i,
  )
  const websiteMatch = html.match(
    /<!-- Website -->\s*<p[^>]*>\s*<a[^>]*\shref="([^"]*)"[^>]*>\s*([\s\S]*?)\s*<\/a>/i,
  )
  const footerMatch = html.match(
    /<!-- Footer -->\s*<div[^>]*>\s*([\s\S]*?)\s*<\/div>/i,
  )
  const disclaimerMatch = html.match(
    /<!-- Legal Disclaimer -->\s*<div[^>]*>\s*([\s\S]*?)\s*<\/div>/i,
  )

  if (!logoMatch && !addressMatch && !websiteMatch && !footerMatch && !disclaimerMatch) {
    return null
  }

  return {
    logoUrl: unescapeHtml(logoMatch?.[1] ?? DEFAULT_SIGNATURE_FIELDS.logoUrl),
    logoAlt: unescapeHtml(logoAltMatch?.[1] ?? DEFAULT_SIGNATURE_FIELDS.logoAlt),
    address: unescapeHtml((addressMatch?.[1] ?? DEFAULT_SIGNATURE_FIELDS.address).trim()),
    websiteUrl: unescapeHtml(websiteMatch?.[1] ?? DEFAULT_SIGNATURE_FIELDS.websiteUrl),
    websiteLabel: unescapeHtml(
      (websiteMatch?.[2] ?? DEFAULT_SIGNATURE_FIELDS.websiteLabel).trim(),
    ),
    footer: unescapeHtml((footerMatch?.[1] ?? DEFAULT_SIGNATURE_FIELDS.footer).trim()),
    disclaimer: unescapeHtml(
      (disclaimerMatch?.[1] ?? DEFAULT_SIGNATURE_FIELDS.disclaimer).trim(),
    ),
  }
}

export function signatureMatchesFields(html: string, fields: EmailSignatureFields): boolean {
  return html.trim() === buildSignatureHtml(fields).trim()
}
